
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Search, Loader2 } from 'lucide-react';
import { ZoneNode, extractZips } from '../utils/zoneUtils';
import { useGeoData } from '../contexts/GeoDataContext';
import { cn } from '../utils/cn';

// ---------------------------------------------------------------------------
// TYPES
// ---------------------------------------------------------------------------
export type ZoneType = 'CO' | 'ST' | 'RE' | 'CI' | 'DI' | 'TO' | 'QU' | 'ZI' | 'Radius' | 'Manual' | 'Street' | 'Country' | 'State' | 'Region' | 'City' | 'District' | 'Town' | 'Zip';

export interface ZoneSearchResult {
  id: string;
  type: ZoneType;
  label: string;
  parents: string[]; // breadcrumb labels e.g. ["Hamburg", "Altona"]
  node: ZoneNode;    // Reference to the original node (for adding children later)
}

export interface BulkImportResult {
  added: number;
  notFound: string[];
  duplicates: number;
}

interface Props {
  onSelectZone: (zone: ZoneNode) => void;
  onBulkSelectZones?: (nodes: ZoneNode[]) => BulkImportResult;
  disabled?: boolean;
  alreadyCoveredZips?: Set<string>;
  /** Focus the input on mount (opened via "+ Ort hinzufügen"). */
  autoFocus?: boolean;
}

const TYPE_MAPPING: Record<string, string> = {
  'CO': 'Land',
  'ST': 'Bundesland',
  'RE': 'Bezirk',
  'DI': 'Stadtteil',
  'CI': 'Stadt',
  'TO': 'Gemeinde',
  'QU': 'Ortsteil',
  'ZI': 'PLZ'
};

// ---------------------------------------------------------------------------
// COMPONENT
// ---------------------------------------------------------------------------

export const ZoneSearch: React.FC<Props> = ({ onSelectZone, onBulkSelectZones, disabled, alreadyCoveredZips, autoFocus }) => {
  const { zoneDefinitions, isLoadingZones, loadZones, errorZones } = useGeoData();

  const [searchTerm, setSearchTerm] = useState('');
  const [results, setResults] = useState<ZoneSearchResult[]>([]);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [showDropdown, setShowDropdown] = useState(false);

  // 1. Load Data
  useEffect(() => {
    loadZones();
  }, [loadZones]);

  const isLoading = isLoadingZones;
  const inputRef = useRef<HTMLInputElement>(null);

  // `autoFocus` alone misses: the input is disabled while zone definitions load, and a
  // disabled input can't take focus. Focus once it becomes usable.
  useEffect(() => {
    if (autoFocus && !disabled && !isLoading) inputRef.current?.focus();
  }, [autoFocus, disabled, isLoading]);
  const error = errorZones;

  const data = useMemo(() => zoneDefinitions || [], [zoneDefinitions]);

  // 2. Flatten Data for Search Index (Memoized)
  const searchIndex = useMemo(() => {
    if (!data.length) return [];

    const index: ZoneSearchResult[] = [];

    const traverse = (nodes: ZoneNode[], parents: string[]) => {
      for (const node of nodes) {
        if (node.t !== 'CO' && node.l) {
             index.push({
                id: node.i,
                type: node.t as ZoneType,
                label: node.l,
                parents,
                node
             });
        }

        if (node.c) {
          const newParents = node.l ? [...parents, node.l] : parents;
          traverse(node.c, newParents);
        }
      }
    };

    traverse(data, []);
    return index;
  }, [data]);

  // 3. Search Handler
  useEffect(() => {
    if (!searchTerm || searchTerm.length < 2) {
      setResults([]);
      setActiveIndex(-1);
      return;
    }

    const lowerTerm = searchTerm.toLowerCase();
    const isNumeric = /^\d+$/.test(searchTerm);

    // Weighted search
    const matches = searchIndex
      .filter(item => {
          const lowerLabel = item.label.toLowerCase();
          if (isNumeric) {
              if (searchTerm.length === 5) return lowerLabel === lowerTerm;
              return lowerLabel.startsWith(lowerTerm);
          }
          return lowerLabel.includes(lowerTerm);
      })
      .map(item => {
          const lowerLabel = item.label.toLowerCase();
          let score = 0;
          if (lowerLabel === lowerTerm) score += 1000;
          else if (lowerLabel.startsWith(lowerTerm)) score += 200;
          else score += 10;

          if (isNumeric && (item.type === 'ZI' || item.type === 'Zip')) score += 500;
          else if (item.type === 'CI' || item.type === 'City') score += 50;

          return { item, score };
      })
      .sort((a, b) => b.score - a.score)
      .map(res => res.item)
      .slice(0, 20);

    setResults(matches);
    setActiveIndex(matches.length > 0 ? 0 : -1);
  }, [searchTerm, searchIndex]);

  // 4. Selection Handler
  const handleSelect = (item: ZoneSearchResult) => {
    if (item.type === 'CO' || item.type === 'ST') return;

    onSelectZone(item.node);
    setSearchTerm('');
    setResults([]);
    setActiveIndex(-1);
    setShowDropdown(false);
  };

  // 5. Bulk PLZ Paste Handler
  const [bulkMessage, setBulkMessage] = useState<{ text: string; type: 'success' | 'warning' } | null>(null);

  const parseBulkPlz = (text: string): string[] => {
    // Split by comma, semicolon, space, tab, newline
    const tokens = text.split(/[,;\s\t\n]+/).map(t => t.trim()).filter(Boolean);
    // Keep only valid 5-digit German PLZ
    return Array.from(new Set(tokens.filter(t => /^\d{5}$/.test(t))));
  };

  const executeBulkImport = (plzList: string[]) => {
    if (!onBulkSelectZones) {
      // Fallback: add one at a time
      for (const plz of plzList) {
        const match = searchIndex.find(
          item => (item.type === 'ZI' || item.type === 'Zip') && item.label === plz
        );
        if (match) onSelectZone(match.node);
      }
      setSearchTerm('');
      return;
    }

    // Look up each PLZ in the search index
    const foundNodes: ZoneNode[] = [];
    const notFound: string[] = [];

    for (const plz of plzList) {
      const match = searchIndex.find(
        item => (item.type === 'ZI' || item.type === 'Zip') && item.label === plz
      );
      if (match) {
        foundNodes.push(match.node);
      } else {
        notFound.push(plz);
      }
    }

    if (foundNodes.length === 0) {
      setBulkMessage({ text: `Keine der ${plzList.length} PLZ konnte zugeordnet werden.`, type: 'warning' });
    } else {
      const result = onBulkSelectZones(foundNodes);
      const parts: string[] = [];
      if (result.added > 0) parts.push(`${result.added} PLZ hinzugefügt`);
      if (result.duplicates > 0) parts.push(`${result.duplicates} bereits vorhanden`);
      if (result.notFound.length > 0) parts.push(`${result.notFound.length} nicht gefunden`);
      if (notFound.length > 0) parts.push(`${notFound.length} unbekannt`);

      setBulkMessage({
        text: parts.join(', '),
        type: result.added > 0 ? 'success' : 'warning',
      });
    }

    setSearchTerm('');
    setResults([]);
    setShowDropdown(false);

    // Auto-clear message after 5 seconds
    setTimeout(() => setBulkMessage(null), 5000);
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    const pasted = e.clipboardData.getData('text');
    const plzList = parseBulkPlz(pasted);

    // If fewer than 2 PLZ detected, fall through to normal search behavior
    if (plzList.length < 2) return;

    e.preventDefault();
    executeBulkImport(plzList);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
      // On Enter with no dropdown results: try bulk PLZ import from typed text
      if (e.key === 'Enter' && results.length === 0 && searchTerm.trim()) {
          const plzList = parseBulkPlz(searchTerm);
          if (plzList.length >= 1) {
              e.preventDefault();
              executeBulkImport(plzList);
              return;
          }
      }

      if (results.length === 0) return;

      if (e.key === 'ArrowDown') {
          e.preventDefault();
          setActiveIndex(prev => (prev + 1) % results.length);
      } else if (e.key === 'ArrowUp') {
          e.preventDefault();
          setActiveIndex(prev => (prev - 1 + results.length) % results.length);
      } else if (e.key === 'Enter' && activeIndex >= 0) {
          e.preventDefault();
          handleSelect(results[activeIndex]);
      } else if (e.key === 'Escape' && showDropdown && results.length > 0) {
          e.stopPropagation();
          e.nativeEvent.stopImmediatePropagation();
          setShowDropdown(false);
      }
  };

  return (
    <div className="relative w-full">
        <div className="relative">
            <input
                type="text"
                ref={inputRef}
                aria-label="Ort, Stadtteil oder PLZ suchen"
                placeholder="Ort, Stadtteil oder PLZ"
                value={searchTerm}
                onChange={e => {
                    setSearchTerm(e.target.value);
                    setShowDropdown(true);
                }}
                onFocus={() => setShowDropdown(true)}
                onPaste={handlePaste}
                onKeyDown={handleKeyDown}
                disabled={disabled || isLoading}
                className="zone-search-input h-11 w-full rounded-control border border-line-strong bg-card px-3 pr-9 text-base text-ink placeholder:text-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand disabled:cursor-not-allowed disabled:opacity-50"
            />
            <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-muted" aria-hidden="true">
                {isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
            </span>
        </div>

        {error && <p className="mt-1.5 text-sm text-danger-text">{error}</p>}

        {bulkMessage && (
            <p
              className={cn(
                'mt-1.5 rounded-control px-3 py-2 text-sm',
                bulkMessage.type === 'success' ? 'bg-success-surface text-success-text' : 'bg-warning-surface text-warning-text'
              )}
            >
                {bulkMessage.text}
            </p>
        )}

        {showDropdown && results.length > 0 && (
            <ul className="search-results-dropdown absolute inset-x-0 top-full z-[1001] mt-1 max-h-[300px] overflow-y-auto rounded-card border border-line bg-card shadow-card">
                {results.map((res, index) => {
                    const isSelectable = res.type !== 'CO' && res.type !== 'ST';
                    const isActive = index === activeIndex;
                    const isAlreadyCovered = alreadyCoveredZips && alreadyCoveredZips.size > 0 && (() => {
                        const zips = extractZips(res.node);
                        return zips.length > 0 && zips.every(z => alreadyCoveredZips.has(z));
                    })();

                    return (
                        <li
                            key={res.id}
                            onClick={() => isSelectable && handleSelect(res)}
                            onMouseEnter={() => setActiveIndex(index)}
                            className={cn(
                              'border-b border-line px-4 py-2.5 last:border-b-0',
                              isSelectable ? 'cursor-pointer hover:bg-sunken' : 'cursor-default opacity-70',
                              isActive && isSelectable && 'bg-sunken'
                            )}
                            title={!isSelectable ? 'Bundesländer und Länder können nicht direkt gewählt werden.' : isAlreadyCovered ? 'Auch in einer anderen deiner Suchen enthalten, trotzdem wählbar (z. B. für andere Filter oder Kanäle)' : ''}
                        >
                            <div className="flex items-center justify-between gap-2">
                                <span className="font-medium text-ink">{res.label}</span>
                                <span className="flex shrink-0 items-center gap-1.5">
                                    {/* Informational only: overlapping searches are allowed (different filters/channels). */}
                                    {isAlreadyCovered && <span className="text-xs text-muted">auch in anderer Suche</span>}
                                    <span className="rounded bg-sunken px-1.5 py-0.5 text-xs font-semibold text-muted">{TYPE_MAPPING[res.type] || res.type}</span>
                                </span>
                            </div>
                            {res.parents.length > 0 && (
                                <div className="mt-0.5 text-xs text-muted">
                                    {res.parents.slice(-3).join(' › ')}
                                </div>
                            )}
                        </li>
                    );
                })}
            </ul>
        )}
    </div>
  );
};
